import React, { useState, useEffect } from 'react';
import { getAllUsers, createGroup } from '../../services/api';
import { Icons } from '../Icons/Icons';
import t from '../../i18n/useTranslation';
import useModalClose from '../../hooks/useModalClose';
import './CreateGroupModal.css';

function CreateGroupModal({ currentUser, onClose, onGroupCreated }) {
    useModalClose(onClose);
    const [groupName, setGroupName] = useState('');
    const [contacts, setContacts] = useState([]);
    const [selectedContacts, setSelectedContacts] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadContacts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadContacts = async () => {
        try {
            const response = await getAllUsers();
            const users = response.data.users.filter(u => u._id !== currentUser._id);
            setContacts(users);
        } catch (error) {
            console.error('Error al cargar usuarios:', error);
        }
    };

    const toggleContact = (contactId) => {
        if (selectedContacts.includes(contactId)) {
            setSelectedContacts(selectedContacts.filter(id => id !== contactId));
        } else {
            setSelectedContacts([...selectedContacts, contactId]);
        }
    };

    const handleCreate = async () => {
        if (!groupName.trim()) { alert(t.createGroup.error.noName); return; }
        if (selectedContacts.length === 0) { alert(t.createGroup.error.noMembers); return; }
        setLoading(true);
        try {
            const members = [currentUser._id, ...selectedContacts];
            await createGroup({ name: groupName, createdBy: currentUser._id, members });
            onGroupCreated();
            onClose();
        } catch (error) {
            console.error('Error al crear grupo:', error);
            alert(t.createGroup.error.create);
        }
        setLoading(false);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{t.createGroup.title}</h2>
                    <button className="close-button" onClick={onClose}>
                        <Icons.Close size={18} color="var(--text-secondary)" />
                    </button>
                </div>

                <div className="modal-body">
                    <div className="form-group">
                        <label>{t.createGroup.groupName}</label>
                        <input
                            type="text"
                            className="group-name-input"
                            placeholder={t.createGroup.groupNamePlaceholder}
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                            maxLength={50}
                        />
                    </div>

                    <div className="form-group">
                        <label>
                            {t.createGroup.selectMembers}
                            {selectedContacts.length > 0 && (
                                <span className="selected-count"> ({selectedContacts.length})</span>
                            )}
                        </label>
                        <div className="contacts-list">
                            {contacts.length === 0 ? (
                                <div className="no-contacts">{t.createGroup.noUsers}</div>
                            ) : (
                                contacts.map(user => (
                                    <div
                                        key={user._id}
                                        className={`contact-item ${selectedContacts.includes(user._id) ? 'selected' : ''}`}
                                        onClick={() => toggleContact(user._id)}
                                    >
                                        <div className="contact-avatar">
                                            {user.name?.charAt(0) || '?'}
                                        </div>
                                        <div className="contact-info">
                                            <div className="contact-name">{user.name}</div>
                                            <div className="contact-phone">{user.language} — {user.region}</div>
                                        </div>
                                        <div className="contact-checkbox">
                                            {selectedContacts.includes(user._id) && <Icons.Check size={12} color="white" />}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="cancel-button" onClick={onClose}>
                        {t.createGroup.cancel}
                    </button>
                    <button
                        className="create-button"
                        onClick={handleCreate}
                        disabled={loading || !groupName.trim() || selectedContacts.length === 0}
                    >
                        {loading ? t.createGroup.creating : <><Icons.Group size={16} color="white" /> {t.createGroup.create}</>}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CreateGroupModal;